import { motion } from "framer-motion";
import {
  Compass,
  Sparkles,
  ShieldCheck,
  Landmark,
  Users2,
  LineChart,
} from "lucide-react";

const items = [
  {
    icon: Compass,
    title: "Proprietary Deal Flow",
    body:
      "Access to curated venture, private, and co-investment opportunities across sectors and geographies.",
    span: "md:col-span-6 md:row-span-2",
    accent: "gold",
  },
  {
    icon: LineChart,
    title: "Operational Alpha",
    body:
      "Portfolio company value creation through operator-led interventions and shared services.",
    span: "md:col-span-3",
    accent: "emerald",
  },
  {
    icon: Sparkles,
    title: "AI & GenAI Fluency",
    body:
      "Applied AI in diligence, deal sourcing, and portfolio operations — not decks.",
    span: "md:col-span-3",
    accent: "gold",
  },
  {
    icon: ShieldCheck,
    title: "Cybersecurity Posture",
    body: "Family-office-grade OpSec, data rooms, and privileged access controls.",
    span: "md:col-span-4",
    accent: "emerald",
  },
  {
    icon: Landmark,
    title: "Governance & Compliance",
    body:
      "Board-ready reporting, cross-jurisdictional structuring, and audit trails.",
    span: "md:col-span-4",
    accent: "gold",
  },
  {
    icon: Users2,
    title: "Next-Gen Alignment",
    body:
      "Structured on-ramps for the next generation — from thesis-writing to allocations.",
    span: "md:col-span-4",
    accent: "emerald",
  },
];

export default function Imperatives() {
  return (
    <section
      className="relative py-24 md:py-40 bg-ink-2 border-t border-[var(--line)]"
      data-testid="imperatives-section"
    >
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-4">
              II. Market Imperatives
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
              Six pressures reshaping every serious allocator&rsquo;s operating stack.
            </h2>
          </div>
          <p className="text-ivory/60 max-w-md md:text-right">
            Each imperative is an entire discipline. Together, they are a
            department. We build them as one.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-[minmax(200px,auto)] gap-3 md:gap-4">
          {items.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: [0.2, 0.7, 0.2, 1] }}
              className={`group relative border border-[var(--line)] bg-ink/40 p-6 md:p-8 hover:border-gold/60 transition-colors ${it.span}`}
              data-testid={`imperative-${i}`}
            >
              <div className="flex items-start justify-between">
                <it.icon
                  size={28}
                  strokeWidth={1.25}
                  className={
                    it.accent === "gold" ? "text-gold" : "text-emerald"
                  }
                />
                <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                  0{i + 1}
                </span>
              </div>
              <div className="mt-8 md:mt-12">
                <h3 className="font-display text-2xl md:text-3xl leading-tight text-ivory">
                  {it.title}
                </h3>
                <p className="mt-3 text-ivory/60 text-sm md:text-base leading-relaxed">
                  {it.body}
                </p>
              </div>
              <div className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full bg-gold transition-all duration-700" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
